import deepmerge from 'deepmerge';
import {omitNullUndefined} from '../common/omitNullUndefined';
import {TIARA_OBJECT_PROPS_KEY} from './constants';
import {debuggingFeature} from './debuggingFeature.variable';
import {TrackEventObject} from './TrackEventObject.interface';
import {trackEvent} from './trackEvent';
import {currentEventContext} from './variables';

/**
 * 타겟 엘리먼트로부터 상위 엘리먼트를 탐색하며 모든 data-t-obj attribute 를 머지한 뒤 전송합니다
 */
export function aggregateAndSendLog({
  targetElem,
  actionName,
  actionType,
  actionKind,
  customProps,
  eventMeta,
  click,
  ecommerce,
  viewimp_contents,
  search,
  customSection,
  customPage,
}: {
  targetElem?: HTMLElement;
  // Pageview는 TrackPageViewContainer를 통해서만 로깅 가능합니다.
  actionType: Exclude<TrackEventObject['actionType'], 'Pageview'>;
  actionName: TrackEventObject['actionName'];
  actionKind?: TrackEventObject['actionKind'];
  customProps: TrackEventObject['customProps'];
  eventMeta?: TrackEventObject['eventMeta'];
  click?: TrackEventObject['click'];
  ecommerce?: TrackEventObject['ecommerce'];
  viewimp_contents?: TrackEventObject['viewimp_contents'];
  search?: TrackEventObject['search'];
  /** customSection 값을 전달한다면 현재 이벤트 컨텍스트의 section 대신 이 값을 설정합니다. */
  customSection?: TrackEventObject['section'];
  /** customPage 값을 전달한다면 현재 이벤트 컨텍스트의 page 대신 이 값을 설정합니다. */
  customPage?: TrackEventObject['page'];
}) {
  // PV 가 한번도 발생하지 않은 상태에서 Event 를 발생하는 경우. 거의 존재할 일 없다고 보면 됨
  if (!currentEventContext.currentPageView) {
    return;
  }
  const {section, page, page_meta} = currentEventContext.currentPageView;
  const initialTrackEventObj: TrackEventObject = {
    actionType,
    actionName,
    page: customPage || page,
    section: customSection || section,
    ...(actionKind && {actionKind}),
    ...(page_meta && {page_meta}),
    ...(customProps && {customProps}),
    ...(eventMeta && {eventMeta}),
    ...(click && {click}),
    ...(ecommerce && {ecommerce}),
    ...(viewimp_contents && {viewimp_contents}),
    ...(search && {search}),
  };

  let aggregatedTrackEventObj = {};
  // targetElem 없이 직접 보낼수도 있음
  if (targetElem) {
    let currentElem: HTMLElement | null = targetElem;
    const elementStack = [];
    for (; currentElem; currentElem = currentElem.parentElement) {
      elementStack.unshift(currentElem);
    }
    elementStack.forEach(element => {
      const tObjStr = element.getAttribute(TIARA_OBJECT_PROPS_KEY);
      if (tObjStr) {
        let tObj = JSON.parse(tObjStr);
        if ('fromGraphql' in tObj) {
          tObj = omitNullUndefined(tObj);
        }
        aggregatedTrackEventObj = deepmerge(aggregatedTrackEventObj, tObj);
      }
    });
  }

  const trackEventObj: TrackEventObject = deepmerge(
    aggregatedTrackEventObj,
    initialTrackEventObj
  );

  // purge
  if (trackEventObj.actionType === 'ViewImp' && trackEventObj.click) {
    delete trackEventObj.click;
  }
  if (trackEventObj.actionType === 'Event' && trackEventObj.viewimp_contents) {
    delete trackEventObj.viewimp_contents;
  }

  if (debuggingFeature.consoleLog) {
    console.log('trackEventObj %j', trackEventObj);
  }
  trackEvent(trackEventObj);
}
